import { z } from 'zod';
import { apiClient } from '@api/client';
import type { ApiResponseShape, CommonResponse } from '@/types/api';
import {
  forecastChartBundleSchema,
  forecastSchema,
  type Forecast,
  type ForecastChartBundle,
  type ForecastHorizon,
  type ForecastMetric,
  type ListForecastsParams,
} from './schema';

const BASE_PATH = '/forecasts';

const listResponseSchema = z.object({
  message: z.string(),
  columnDefs: z.array(z.record(z.unknown())).optional(),
  rowData: z.array(forecastSchema),
  gridOptions: z.record(z.unknown()).optional(),
  paginationDetails: z
    .object({
      page: z.number(),
      pageSize: z.number(),
      totalRecords: z.number(),
      isLastPage: z.boolean(),
    })
    .optional(),
});

const chartResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: forecastChartBundleSchema.nullable(),
  error: z.string().nullable().optional(),
});

const recordResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: forecastSchema.nullable(),
  error: z.string().nullable().optional(),
});

export interface UpdateForecastPayload {
  isActive?: boolean;
  notes?: string | null;
  predictedMin?: number | null;
  predictedMax?: number | null;
}

function pruneParams(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

export async function listForecasts(
  params: ListForecastsParams,
): Promise<CommonResponse<Forecast>> {
  const response = await apiClient.get(BASE_PATH, {
    params: pruneParams(params as unknown as Record<string, unknown>),
  });
  const parsed = listResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(`Invalid list response: ${parsed.error.message}`);
  }
  return parsed.data;
}

export async function getForecastChart(
  priceSeriesId: string,
  metric: ForecastMetric,
  horizon: ForecastHorizon,
): Promise<ForecastChartBundle> {
  const response = await apiClient.get(
    `${BASE_PATH}/series/${priceSeriesId}/chart`,
    { params: { metric, horizon } },
  );
  const parsed = chartResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function updateForecast(
  id: string,
  payload: UpdateForecastPayload,
): Promise<Forecast> {
  const response = await apiClient.put<ApiResponseShape<Forecast>>(
    `${BASE_PATH}/${id}`,
    payload,
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty update response' : parsed.error.message);
  }
  return parsed.data.data;
}
